/**
 * useAiTelemetry — Wave 1 debt closeout.
 *
 * TanStack Query hook for GET /api/admin/ai/telemetry. Renders inside
 * <AdminDashboardPanel> (§AI-drift section), which is only reachable by an
 * admin, so the route's requireAdmin gate is the authority — a non-admin /
 * expired session surfaces as a 403 throw here and the section shows its
 * inline error state.
 *
 * Counters are ephemeral (since the API booted), so `staleTime: 0`: always
 * treat as stale and refetch when the panel mounts. Family-scale data — no
 * cache pressure.
 *
 * credentials:'same-origin' mirrors usePushHealth — the panel is always served
 * same-origin; never send cookies cross-origin.
 */
import { useQuery } from '@tanstack/react-query';

export interface AiTelemetrySnapshot {
  since: string;
  messages_flushed: number;
  shopping_added: number;
  corrections: number;
  briefings_generated: number;
  briefings_corrected: number;
  /** Notatforslag nattjobben har lagret som ventende siden oppstart (#196). */
  memory_suggested: number;
  tools: Array<{ tool: string; count: number; avg_ms: number; max_ms: number }>;
  corrected_allergens: Array<{ allergen: string; count: number }>;
}

export function useAiTelemetry() {
  return useQuery<AiTelemetrySnapshot>({
    queryKey: ['admin', 'ai', 'telemetry'],
    staleTime: 0,
    queryFn: async () => {
      const res = await globalThis.fetch('/api/admin/ai/telemetry', {
        credentials: 'same-origin',
      });
      if (!res.ok) {
        throw new Error(`ai-telemetry-${res.status}`);
      }
      return (await res.json()) as AiTelemetrySnapshot;
    },
  });
}
